// Howard University: public college page (#2748), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Howard's Common Data Set 2025-2026 ("CDS PDF 2526_06.25.26.pdf"),
//    linked from Institutional Research and Assessment's Common Data Set
//    page (ira.howard.edu): C1 applicants, admits and enrollees for Fall
//    2025 with the residency table (in-state means the District of
//    Columbia; 60 applicants and 22 admits of unknown residency are in the
//    totals but in no row); C8 test policy for Fall 2027; C21/C22 early
//    plans; G1/G5 2026-2027 costs (G0's "not yet available" box is not
//    ticked). Quirk: C21's early decision counts (430 applied, 363
//    admitted) sit under the form's heading "For the Fall 2024 entering
//    class", but the 2024-2025 edition reports different Fall 2024 counts
//    (348 and 172), so they are read here as Fall 2025, the class the rest
//    of this edition covers. The CDS gives November 15 for the early
//    deadlines; Admissions' current page says November 1, and the page wins.
//  - Howard Office of Admission, Domestic First Year Admissions ("Fall 2027
//    Application now open"): deadlines, decision release, the early
//    decision terms, the test policy "For the 2026-27 admission cycle" and
//    the program requirements for Art, Theatre Arts, Music, Architecture
//    and the joint BS/MD and BS/DDS programs; and Undergraduate Academic
//    Programs (admission into the first or second choice major).
//  - College Scorecard, school id 131520 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Howard University Common Data Set 2025-2026',
        url: 'https://ira.howard.edu/institutional-research/common-data-set',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Howard Office of Admission, Domestic First Year Admissions',
        url: 'https://admission.howard.edu/undergraduate/first-year',
        read: '2026-10-01',
    },
    programs: {
        label: 'Howard Office of Admission, Undergraduate Academic Programs',
        url: 'https://admission.howard.edu/undergraduate/academic-programs',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?131520-Howard-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'howard-university',
    kbId: 'howard_university',
    name: 'Howard University',
    shortName: 'Howard',
    location: 'Washington, DC',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Howard colors and typography page); decoration only.
    branding: {
        primary: '#003A63',
        secondary: '#E51937',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/howard_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 37270,
        admitted: 15724,
        enrolled: 2490,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 668, admitted: 258 },
            { group: 'Out-of-state', applied: 34472, admitted: 14206 },
            { group: 'International', applied: 2070, admitted: 1238 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Howard publishes no acceptance rate by school or major. Its Office of Admission says students are generally admitted into their first or second choice major, as named on the Common App.',
        source: 'programs',
    },
    earlyRounds: {
        year: 'Fall 2025',
        title: 'Early decision',
        rows: [
            { plan: 'Early Decision', applied: 430, admitted: 363 },
        ],
        note: 'Early decision counts as the 2025-2026 Common Data Set reports them; they are part of the totals above. Howard also has a nonbinding early action plan, but the Common Data Set does not report early action counts, so there is no early action acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You name a first and second choice major on the Common App. Some programs ask for more:',
        rows: [
            { school: 'Art (Studio Art)', requirement: 'A portfolio of 12 to 15 images and videos of original work, sent through SlideRoom.' },
            { school: 'Theatre Arts', requirement: 'Acting, Dance and Musical Theatre applicants send a recorded audition, take part in a live audition and must apply by November 1. Theatre Arts Administration and Theatre Technology applicants may apply by the February 1 Regular Decision deadline.' },
            { school: 'Music', requirement: 'A voice or instrument audition, by appointment; recordings are accepted from applicants who cannot audition in person.' },
            { school: 'Architecture & Design Studies', requirement: 'A portfolio of one to three examples of creative work (graphic, written or audiovisual), sent through SlideRoom or your Howard admissions portal.' },
            { school: 'BS/MD and BS/DDS programs', requirement: 'An additional program essay in the Common App writing section. The test-optional policy does not extend to the joint degree programs (BS/MD, BS/DDS, BA/JD).' },
        ],
        note: 'Art, Theatre Arts and Music applicants are admitted conditionally until the department reviews the portfolio or audition, so Howard asks them to think carefully about their second choice major.',
        source: 'firstYear',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'February 1' },
        rows: [
            { plan: 'Early Action', deadline: 'November 1', decision: 'Late December', binding: false },
            { plan: 'Early Decision', deadline: 'November 1', decision: 'Late December', binding: true },
            { plan: 'Regular Decision', deadline: 'February 1', decision: 'Early April', binding: false },
        ],
        note: 'Documents (test scores, recommendations, records) are due by the same date as the application. Admitted students reply and deposit by May 1. Early applications still incomplete after November 1 move to Regular Decision. You apply through the Common App, with a $45 fee or a Common App fee waiver. Howard has no spring entry for first-year students.',
        source: 'firstYear',
        binding: 'Early decision is binding and is for students whose first choice is Howard: you may apply to other colleges, but early decision to only one, and if admitted you generally must withdraw all other applications. If you seek aid, you need not withdraw them until you hear about financial aid, and if Howard has not met your full need, enrollment is not required. Your counselor and a parent sign the ED Agreement.',
        bindingSource: 'firstYear',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'For the 2026-27 admission cycle, SAT and ACT scores are not required, and students who do not send them are not at a disadvantage, including for the Karsh STEM program and for scholarships. The joint degree programs (BS/MD, BS/DDS, BA/JD) are not covered. Only official scores sent by the testing agency count; scores you enter yourself, counselor reports and scores on transcripts are not considered. Howard does not superscore the ACT. October is the last test date considered for early applicants and December for Regular Decision.',
        source: 'firstYear',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 37996 },
            { item: 'Required fees', amount: 940 },
            { item: 'Food and housing (on campus)', amount: 18982 },
            { item: 'Books and supplies', amount: 1220 },
            { item: 'Transportation', amount: 1154 },
            { item: 'Other expenses', amount: 2494 },
        ],
        total: 62786,
        note: 'For a full-time undergraduate living on campus, fall and spring.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 33002,
            byIncome: [
                { income: '$0 to $30,000', amount: 28321 },
                { income: '$30,001 to $48,000', amount: 28262 },
                { income: '$48,001 to $75,000', amount: 30273 },
                { income: '$75,001 to $110,000', amount: 31722 },
                { income: 'Over $110,000', amount: 41046 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Howard\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
